<template>
  <div class="wsim">
    <!-- ── Progress Ring at Top ── -->
    <div class="wsim-progress-bar">
      <div class="wsim-progress-track">
        <div
          class="wsim-progress-fill"
          :style="{ width: `${(doneActions.length / 3) * 100}%` }"
        ></div>
      </div>
      <div class="wsim-progress-label">
        <span class="wsim-progress-count">{{ doneActions.length }}</span>
        <span class="wsim-progress-sep">/</span>
        <span class="wsim-progress-total">3</span>
        <span class="wsim-progress-word">{{
          doneActions.length >= 3 ? '— Nice work! 🎉' : 'actions to try'
        }}</span>
      </div>
    </div>

    <!-- ── MAIN MENU VIEW ── -->
    <div v-if="view === 'menu'" class="wsim-menu">
      <div class="wsim-heading">
        <h2 class="wsim-title">Simulate a Customer Action</h2>
        <p class="wsim-sub">
          Tap an action below to trigger the AI — watch it respond in real-time.
        </p>
      </div>

      <div class="wsim-grid">
        <button
          v-for="action in actions"
          :key="action.type"
          class="wsim-card"
          :class="{
            'wsim-card-done': doneActions.includes(action.type),
            'wsim-card-busy': loading === action.type,
            'wsim-card-disabled':
              (action.type === 'purchase' || action.type === 'abandon_cart') &&
              !hasCart
          }"
          :disabled="
            !!loading ||
            ((action.type === 'purchase' || action.type === 'abandon_cart') &&
              !hasCart)
          "
          @click="handleAction(action)"
        >
          <div class="wsim-card-badge" v-if="doneActions.includes(action.type)">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M2.5 7L5.5 10L11.5 4"
                stroke="white"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="wsim-card-spin" v-if="loading === action.type"></div>
          <div class="wsim-card-emoji" :class="action.colorClass">
            {{ action.emoji }}
          </div>
          <div class="wsim-card-text">
            <div class="wsim-card-name">
              {{ action.label }}
              <span
                v-if="action.type === 'add_to_cart' && cartCount > 0"
                class="wsim-cart-count"
              >
                {{ cartCount }}
              </span>
            </div>
            <div class="wsim-card-hint">
              {{
                (action.type === 'purchase' ||
                  action.type === 'abandon_cart') &&
                !hasCart
                  ? 'Add something to cart first'
                  : action.hint
              }}
            </div>
          </div>
        </button>
      </div>
    </div>

    <!-- ── PRODUCT PICKER VIEW ── -->
    <div v-else-if="view === 'products'" class="wsim-sub-view">
      <h3 class="wsim-sub-title">
        {{
          productMode === 'add_to_cart'
            ? 'Pick a product to add'
            : 'Pick a product to view'
        }}
      </h3>
      <div class="wsim-search-wrap">
        <svg
          class="wsim-search-icon"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <circle cx="7" cy="7" r="5.5" stroke="#9ca3af" stroke-width="1.5" />
          <path
            d="M11 11L14 14"
            stroke="#9ca3af"
            stroke-width="1.5"
            stroke-linecap="round"
          />
        </svg>
        <input
          v-model="searchQuery"
          class="wsim-search"
          placeholder="Search products..."
          type="text"
        />
      </div>
      <div class="wsim-products">
        <!-- Skeleton loading -->
        <template v-if="productsLoading">
          <div v-for="i in 6" :key="'skel-' + i" class="wsim-prod wsim-skel">
            <div class="wsim-skel-img"></div>
            <div class="wsim-skel-line wsim-skel-name"></div>
            <div class="wsim-skel-line wsim-skel-price"></div>
          </div>
        </template>
        <div v-else-if="filteredProducts.length === 0" class="wsim-empty">
          No products found
        </div>
        <button
          v-for="prod in filteredProducts"
          :key="prod.id"
          class="wsim-prod"
          :class="{ 'wsim-prod-busy': loading === prod.id }"
          :disabled="!!loading"
          @click="pickProduct(prod)"
        >
          <div class="wsim-prod-img">
            <img
              v-if="prod.featuredImage?.url"
              :src="prod.featuredImage.url"
              :alt="prod.title"
            />
            <div v-else class="wsim-prod-placeholder">📦</div>
            <div v-if="loading === prod.id" class="wsim-prod-loader"></div>
            <div
              v-if="productMode === 'add_to_cart' && cartCountFor(prod.id) > 0"
              class="wsim-prod-qty"
            >
              {{ cartCountFor(prod.id) }}
            </div>
          </div>
          <div class="wsim-prod-info">
            <div class="wsim-prod-name">{{ prod.title }}</div>
            <div class="wsim-prod-price">{{ formatPrice(prod) }}</div>
          </div>
        </button>
      </div>
    </div>

    <!-- ── SEARCH VIEW ── -->
    <div v-else-if="view === 'search'" class="wsim-sub-view">
      <h3 class="wsim-sub-title">What did the customer search?</h3>
      <div class="wsim-search-input-area">
        <input
          v-model="searchInput"
          class="wsim-big-input"
          placeholder="e.g. lavender soap, soy candles..."
          type="text"
          @keyup.enter="submitSearch"
        />
        <button
          class="wsim-submit-btn"
          :disabled="!!loading || !searchInput.trim()"
          @click="submitSearch"
        >
          {{ loading === 'search' ? 'Searching...' : 'Search →' }}
        </button>
      </div>
      <div class="wsim-suggestions">
        <span
          v-for="s in searchSuggestions"
          :key="s"
          class="wsim-suggestion"
          @click="searchInput = s"
        >
          {{ s }}
        </span>
      </div>
    </div>

    <!-- ── FORM VIEW ── -->
    <div v-else-if="view === 'form'" class="wsim-sub-view">
      <h3 class="wsim-sub-title">Customer Survey</h3>
      <div class="wsim-form">
        <div v-for="(q, idx) in formQuestions" :key="idx" class="wsim-field">
          <label class="wsim-label">{{ q.question }}</label>

          <!-- Yes / No toggle -->
          <div v-if="q.type === 'yesno'" class="wsim-yesno">
            <button
              class="wsim-yn-btn"
              :class="{ active: formAnswers[idx] === 'Yes' }"
              @click="formAnswers[idx] = 'Yes'"
            >
              Yes
            </button>
            <button
              class="wsim-yn-btn"
              :class="{ active: formAnswers[idx] === 'No' }"
              @click="formAnswers[idx] = 'No'"
            >
              No
            </button>
          </div>

          <!-- Multiple choice pills -->
          <div v-else-if="q.type === 'choice'" class="wsim-choices">
            <button
              v-for="opt in q.options"
              :key="opt"
              class="wsim-choice-pill"
              :class="{ active: formAnswers[idx] === opt }"
              @click="formAnswers[idx] = opt"
            >
              {{ opt }}
            </button>
          </div>

          <!-- Free text -->
          <input
            v-else
            v-model="formAnswers[idx]"
            class="wsim-input"
            type="text"
            placeholder="Type anything..."
          />
        </div>
        <button
          class="wsim-submit-btn"
          :disabled="!!loading || !hasAnyAnswer"
          @click="submitForm"
        >
          {{ loading === 'form_submit' ? 'Submitting...' : 'Submit Form →' }}
        </button>
      </div>
    </div>

    <!-- ── Toast ── -->
    <transition name="wsim-toast-anim">
      <div v-if="toast" class="wsim-toast">{{ toast }}</div>
    </transition>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable no-undef */
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/api/axiosBase';
import { getProject } from '@/api/projects';

const SIM_API = '/v2';
const route = useRoute();

// Unique session ID per walkthrough run so simulated actions always create a new session
const walkthroughSessionId = `wt-session-${Date.now()}-${Math.random()
  .toString(36)
  .slice(2, 8)}`;

// ── Actions config ──
const actions = [
  {
    type: 'page_view',
    emoji: '👁',
    label: 'View Product',
    hint: 'Pick a product they browsed',
    colorClass: 'wsim-c-blue',
    needsPicker: 'products' as const
  },
  {
    type: 'add_to_cart',
    emoji: '🛒',
    label: 'Add to Cart',
    hint: 'Pick a product to add',
    colorClass: 'wsim-c-amber',
    needsPicker: 'products' as const
  },
  {
    type: 'search',
    emoji: '🔍',
    label: 'Search',
    hint: 'Type a search query',
    colorClass: 'wsim-c-violet',
    needsPicker: 'search' as const
  },
  {
    type: 'purchase',
    emoji: '💳',
    label: 'Purchase',
    hint: 'Complete a purchase',
    colorClass: 'wsim-c-green',
    needsPicker: null
  },
  {
    type: 'abandon_cart',
    emoji: '🚪',
    label: 'Abandon Cart',
    hint: 'Leave without buying',
    colorClass: 'wsim-c-red',
    needsPicker: null
  },
  {
    type: 'form_submit',
    emoji: '📋',
    label: 'Submit Form',
    hint: 'Answer a few questions',
    colorClass: 'wsim-c-teal',
    needsPicker: 'form' as const
  }
];

const searchSuggestions = [
  'lavender soap',
  'soy candles',
  'gift sets',
  'charcoal bar'
];

interface FormQuestion {
  question: string;
  type: 'text' | 'yesno' | 'choice';
  options?: string[];
}

const QUESTIONS_POOL: FormQuestion[] = [
  {
    question: 'Which scent family do you prefer?',
    type: 'choice',
    options: ['Lavender', 'Citrus', 'Woodsy', 'Unscented']
  },
  {
    question: 'Are you shopping for yourself or a gift?',
    type: 'choice',
    options: ['Myself', 'A gift', 'Both']
  },
  { question: 'Do you have sensitive skin?', type: 'yesno' },
  {
    question: 'What kind of bar soap do you usually use?',
    type: 'choice',
    options: [
      'Moisturizing',
      'Exfoliating',
      'Charcoal / detox',
      'Whatever smells good'
    ]
  },
  { question: 'Have you tried our products before?', type: 'yesno' },
  {
    question: 'How did you find us?',
    type: 'choice',
    options: [
      'Instagram',
      'TikTok',
      'Google',
      'Friend / word of mouth',
      'Farmers market'
    ]
  },
  {
    question: 'Where do you usually burn candles?',
    type: 'choice',
    options: ['Bedroom', 'Living room', 'Bathroom', 'Office']
  },
  { question: 'What scent reminds you of home?', type: 'text' },
  {
    question: 'How often do you buy soap or candles?',
    type: 'choice',
    options: [
      'Every month',
      'Every few months',
      'A couple times a year',
      'First time'
    ]
  },
  { question: 'Would you like to join our loyalty program?', type: 'yesno' },
  {
    question: 'What matters most to you in a soap?',
    type: 'choice',
    options: ['Ingredients', 'Scent', 'Lather', 'Price']
  },
  { question: 'Any ingredients you want to avoid?', type: 'text' },
  {
    question: 'Prefer soy or beeswax candles?',
    type: 'choice',
    options: ['Soy', 'Beeswax', 'No preference']
  },
  {
    question: 'Anything special we should know about your order?',
    type: 'text'
  }
];

// ── State ──
const view = ref<'menu' | 'products' | 'search' | 'form'>('menu');
const loading = ref<string | null>(null);
const doneActions = ref<string[]>([]);
const toast = ref<string | null>(null);
let toastTimer: ReturnType<typeof setTimeout> | null = null;

const productMode = ref<'page_view' | 'add_to_cart'>('page_view');
const products = ref<any[]>([]);
const productsLoading = ref(false);
const searchQuery = ref('');
const searchInput = ref('');
let productsFetched = false;

const formQuestions = ref<FormQuestion[]>([]);
const formAnswers = ref<string[]>([]);
const hasAnyAnswer = computed(() =>
  formAnswers.value.some((a) => a.trim().length > 0)
);

// Cart state
const cartItems = ref<any[]>([]);
const cartCount = computed(() => cartItems.value.length);
const hasCart = computed(() => cartItems.value.length > 0);

function cartCountFor(productId: string): number {
  return cartItems.value.filter((i: any) => i.id === productId).length;
}

const filteredProducts = computed(() => {
  const q = searchQuery.value.toLowerCase().trim();
  if (!q) return products.value;
  return products.value.filter((p: any) => p.title?.toLowerCase().includes(q));
});

// ── Lifecycle ──
onMounted(() => {
  fetchProducts();
});

// ── Methods ──
function showToast(msg: string) {
  toast.value = msg;
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    toast.value = null;
  }, 2500);
}

async function fetchProducts() {
  if (productsFetched) return;
  const projectID = route.params.projectID as string;
  if (!projectID) return;
  productsLoading.value = true;
  try {
    const project = await getProject(projectID);
    const proj = project as any;
    products.value = proj?.products || [];
    productsFetched = true;
  } catch {
    // silent
  } finally {
    productsLoading.value = false;
  }
}

function handleAction(action: (typeof actions)[number]) {
  if (action.needsPicker === 'products') {
    productMode.value = action.type as 'page_view' | 'add_to_cart';
    searchQuery.value = '';
    view.value = 'products';
  } else if (action.needsPicker === 'search') {
    view.value = 'search';
  } else if (action.needsPicker === 'form') {
    // Pick 2-3 questions, ensuring a mix of types
    const count = Math.random() < 0.5 ? 2 : 3;
    const shuffled = [...QUESTIONS_POOL].sort(() => Math.random() - 0.5);
    // Try to get at least one non-text question
    const nonText = shuffled.filter((q) => q.type !== 'text');
    const textOnly = shuffled.filter((q) => q.type === 'text');
    const picked: FormQuestion[] = [];
    if (nonText.length > 0) picked.push(nonText[0]);
    if (nonText.length > 1 && count === 3) picked.push(nonText[1]);
    for (const q of textOnly) {
      if (picked.length >= count) break;
      picked.push(q);
    }
    // Fill remaining from any if needed
    for (const q of shuffled) {
      if (picked.length >= count) break;
      if (!picked.includes(q)) picked.push(q);
    }
    formQuestions.value = picked.slice(0, count);
    formAnswers.value = new Array(count).fill('');
    view.value = 'form';
  } else {
    fireAction(action.type);
  }
}

async function fireAction(
  actionType: string,
  metadata: Record<string, unknown> = {}
) {
  if (loading.value) return;
  loading.value = actionType;

  const projectID = route.params.projectID as string;
  const customerID = route.params.SimpleID as string;

  if (actionType === 'purchase') {
    const items = cartItems.value;
    metadata.amount = items.reduce(
      (sum: number, i: any) => sum + (Number(i.price) || 0),
      0
    );
    metadata.products = items.map((i: any) => ({
      id: i.id,
      title: i.title,
      price: i.price
    }));
  } else if (actionType === 'abandon_cart') {
    metadata.products = cartItems.value.map((i: any) => ({
      id: i.id,
      title: i.title
    }));
  }

  // Always include walkthrough session ID so actions group into a new session
  metadata.sessionId = walkthroughSessionId;

  try {
    await api.post(`${SIM_API}/project/${projectID}/demo/simulate-action`, {
      customerId: customerID,
      actionType,
      metadata,
      skipAI: true
    });
    if (!doneActions.value.includes(actionType)) {
      doneActions.value.push(actionType);
    }
    if (actionType === 'purchase') {
      const count = cartItems.value.length;
      cartItems.value = [];
      showToast(`💳 Purchased ${count} item${count !== 1 ? 's' : ''}!`);
    } else if (actionType === 'abandon_cart') {
      cartItems.value = [];
      showToast('🚪 Cart abandoned');
    } else {
      const msgs: Record<string, string> = {
        page_view: '👁 Product viewed',
        add_to_cart: '🛒 Added to cart',
        search: '🔍 Search tracked',
        form_submit: '📋 Form submitted'
      };
      showToast(msgs[actionType] || '✓ Done');
    }
  } catch {
    showToast('❌ Failed — try again');
  } finally {
    loading.value = null;
  }
}

async function pickProduct(prod: any) {
  if (loading.value) return;
  loading.value = prod.id;
  const actionType = productMode.value;
  const projectID = route.params.projectID as string;
  const customerID = route.params.SimpleID as string;

  const metadata: Record<string, unknown> = {
    productTitle: prod.title,
    productId: prod.id,
    sessionId: walkthroughSessionId
  };
  if (prod.variants?.[0]?.price) metadata.price = prod.variants[0].price;
  if (prod.featuredImage?.url) metadata.imageUrl = prod.featuredImage.url;

  try {
    await api.post(`${SIM_API}/project/${projectID}/demo/simulate-action`, {
      customerId: customerID,
      actionType,
      metadata,
      skipAI: true
    });
    if (!doneActions.value.includes(actionType)) {
      doneActions.value.push(actionType);
    }
    if (actionType === 'add_to_cart') {
      cartItems.value.push({
        id: prod.id,
        title: prod.title,
        price: prod.variants?.[0]?.price || '0.00',
        imageUrl: prod.featuredImage?.url
      });
      showToast(`🛒 Added ${prod.title} (${cartCount.value} in cart)`);
      view.value = 'menu';
    } else {
      showToast(`👁 Viewed ${prod.title}`);
      view.value = 'menu';
    }
  } catch {
    showToast('❌ Failed — try again');
  } finally {
    loading.value = null;
  }
}

async function submitSearch() {
  const q = searchInput.value.trim();
  if (!q || loading.value) return;
  loading.value = 'search';
  const projectID = route.params.projectID as string;
  const customerID = route.params.SimpleID as string;

  try {
    await api.post(`${SIM_API}/project/${projectID}/demo/simulate-action`, {
      customerId: customerID,
      actionType: 'search',
      metadata: { searchQuery: q, sessionId: walkthroughSessionId },
      skipAI: true
    });
    if (!doneActions.value.includes('search')) {
      doneActions.value.push('search');
    }
    showToast(`🔍 Searched "${q}"`);
    searchInput.value = '';
    view.value = 'menu';
  } catch {
    showToast('❌ Failed — try again');
  } finally {
    loading.value = null;
  }
}

async function submitForm() {
  if (loading.value || !hasAnyAnswer.value) return;
  loading.value = 'form_submit';
  const projectID = route.params.projectID as string;
  const customerID = route.params.SimpleID as string;

  const questions: string[] = [];
  const answers: string[] = [];
  formQuestions.value.forEach((q, i) => {
    questions.push(q.question);
    answers.push(formAnswers.value[i]?.trim() || '');
  });

  try {
    await api.post(`${SIM_API}/project/${projectID}/demo/simulate-action`, {
      customerId: customerID,
      actionType: 'form_submit',
      metadata: { questions, answers, sessionId: walkthroughSessionId },
      skipAI: true
    });
    if (!doneActions.value.includes('form_submit')) {
      doneActions.value.push('form_submit');
    }
    showToast('📋 Form submitted!');
    view.value = 'menu';
  } catch {
    showToast('❌ Failed — try again');
  } finally {
    loading.value = null;
  }
}

function formatPrice(prod: any) {
  const v = prod.variants?.[0];
  if (v?.price) return `$${Number(v.price).toFixed(2)}`;
  if (prod.price) return `$${Number(prod.price).toFixed(2)}`;
  return '';
}

// Allow parent to read state and reset
defineExpose({
  doneActions,
  doneCount: computed(() => doneActions.value.length),
  view,
  searchInput,
  submitSearch,
  submitForm,
  hasAnyAnswer,
  reset() {
    loading.value = null;
    doneActions.value = [];
    cartItems.value = [];
    toast.value = null;
    view.value = 'menu';
    searchQuery.value = '';
    searchInput.value = '';
    if (toastTimer) clearTimeout(toastTimer);
  }
});
</script>

<style scoped>
/* ══════════════════════════════════════
   WALKTHROUGH SIMULATE — PREMIUM
   ══════════════════════════════════════ */
.wsim {
  width: 100%;
  position: relative;
}

/* ── Progress Bar ── */
.wsim-progress-bar {
  margin-bottom: 24px;
}

.wsim-progress-track {
  width: 100%;
  height: 8px;
  background: #f1f5f9;
  border-radius: 100px;
  overflow: hidden;
}

.wsim-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #a855f7, #22c55e);
  border-radius: 100px;
  transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  min-width: 0;
}

.wsim-progress-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  margin-top: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
}

.wsim-progress-count {
  font-size: 18px;
  font-weight: 800;
  color: #6366f1;
}

.wsim-progress-sep {
  color: #cbd5e1;
  font-weight: 400;
}

.wsim-progress-total {
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
}

.wsim-progress-word {
  margin-left: 4px;
  font-weight: 500;
}

/* ── Heading ── */
.wsim-heading {
  text-align: center;
  margin-bottom: 20px;
}

.wsim-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 6px;
  letter-spacing: -0.02em;
}

.wsim-sub {
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}

/* ── Card Grid ── */
.wsim-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.wsim-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 20px 12px 16px;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);
  overflow: hidden;
}

.wsim-card:hover:not(:disabled) {
  transform: translateY(-2px);
  border-color: #a5b4fc;
  box-shadow: 0 8px 24px -4px rgba(99, 102, 241, 0.15);
}

.wsim-card:active:not(:disabled) {
  transform: scale(0.97);
}

.wsim-card:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Done state */
.wsim-card-done {
  border-color: #86efac;
  background: #f0fdf4;
}

.wsim-card-done:hover:not(:disabled) {
  border-color: #4ade80 !important;
  box-shadow: 0 8px 24px -4px rgba(34, 197, 94, 0.18) !important;
}

.wsim-card-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 22px;
  height: 22px;
  background: #22c55e;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: wsim-pop 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes wsim-pop {
  0% {
    transform: scale(0);
  }
  60% {
    transform: scale(1.25);
  }
  100% {
    transform: scale(1);
  }
}

.wsim-card-spin {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  border: 2.5px solid #e2e8f0;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: wsim-spin 0.6s linear infinite;
}

@keyframes wsim-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Emoji bubble */
.wsim-card-emoji {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  transition: transform 0.2s;
}

.wsim-card:hover:not(:disabled) .wsim-card-emoji {
  transform: scale(1.1);
}

.wsim-c-blue {
  background: #dbeafe;
}
.wsim-c-amber {
  background: #fef3c7;
}
.wsim-c-violet {
  background: #ede9fe;
}
.wsim-c-green {
  background: #dcfce7;
}
.wsim-c-red {
  background: #fee2e2;
}
.wsim-c-teal {
  background: #ccfbf1;
}

/* Card text */
.wsim-card-text {
  text-align: center;
}

.wsim-card-name {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.wsim-cart-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  font-size: 11px;
  font-weight: 800;
  border-radius: 10px;
  animation: wsim-pop 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}

.wsim-card-disabled {
  opacity: 0.45;
  cursor: not-allowed !important;
}

.wsim-card-disabled .wsim-card-hint {
  color: #cbd5e1;
}

.wsim-card-hint {
  font-size: 11.5px;
  color: #94a3b8;
  margin-top: 2px;
  line-height: 1.3;
}

/* ── Sub-view shared ── */
.wsim-sub-view {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.wsim-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: #6366f1;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 0;
  width: fit-content;
  transition: opacity 0.15s;
}

.wsim-back:hover {
  opacity: 0.7;
}

.wsim-sub-title {
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
}

/* ── Search ── */
.wsim-search-wrap {
  position: relative;
}

.wsim-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
}

.wsim-search {
  width: 100%;
  padding: 10px 12px 10px 36px;
  font-size: 14px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  font-family: inherit;
}

.wsim-search:focus {
  border-color: #6366f1;
}

/* ── Skeleton loading ── */
.wsim-skel {
  pointer-events: none;
}

.wsim-skel-img {
  width: 72px;
  height: 72px;
  border-radius: 10px;
  background: linear-gradient(110deg, #e2e8f0 30%, #f1f5f9 50%, #e2e8f0 70%);
  background-size: 200% 100%;
  animation: wsim-shimmer 1.4s ease-in-out infinite;
}

.wsim-skel-line {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(110deg, #e2e8f0 30%, #f1f5f9 50%, #e2e8f0 70%);
  background-size: 200% 100%;
  animation: wsim-shimmer 1.4s ease-in-out infinite;
}

.wsim-skel-name {
  width: 80%;
  margin-top: 2px;
}

.wsim-skel-price {
  width: 40%;
  height: 10px;
  margin-top: 2px;
}

@keyframes wsim-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ── Product Grid ── */
.wsim-products {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  max-height: 340px;
  overflow-y: auto;
}

.wsim-prod {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 10px 8px;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.wsim-prod:hover:not(:disabled) {
  transform: translateY(-2px);
  border-color: #a5b4fc;
  box-shadow: 0 6px 18px -4px rgba(99, 102, 241, 0.12);
}

.wsim-prod:active:not(:disabled) {
  transform: scale(0.97);
}

.wsim-prod:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.wsim-prod-img {
  width: 72px;
  height: 72px;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  background: #f1f5f9;
  flex-shrink: 0;
}

.wsim-prod-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.wsim-prod-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.wsim-prod-loader {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
}

.wsim-prod-loader::after {
  content: '';
  width: 22px;
  height: 22px;
  border: 2.5px solid #e2e8f0;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: wsim-spin 0.6s linear infinite;
}

.wsim-prod-qty {
  position: absolute;
  top: 6px;
  right: 6px;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  font-size: 11px;
  font-weight: 800;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(99, 102, 241, 0.4);
  animation: wsim-qty-pop 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes wsim-qty-pop {
  0% {
    transform: scale(0);
  }
  60% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

.wsim-prod-info {
  text-align: center;
}

.wsim-prod-name {
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.wsim-prod-price {
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
  margin-top: 1px;
}

.wsim-prod-busy {
  border-color: #c7d2fe;
  background: #eef2ff;
}

.wsim-empty {
  grid-column: 1 / -1;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
  padding: 28px 0;
}

/* ── Big Search Input ── */
.wsim-search-input-area {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.wsim-big-input {
  width: 100%;
  padding: 14px 16px;
  font-size: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  font-family: inherit;
}

.wsim-big-input:focus {
  border-color: #6366f1;
}

.wsim-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.wsim-suggestion {
  font-size: 12px;
  font-weight: 600;
  padding: 5px 12px;
  background: #f1f5f9;
  color: #475569;
  border-radius: 100px;
  cursor: pointer;
  transition: all 0.15s;
}

.wsim-suggestion:hover {
  background: #e0e7ff;
  color: #4338ca;
}

/* ── Submit Button ── */
.wsim-submit-btn {
  width: 100%;
  padding: 13px 24px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  font-size: 15px;
  font-weight: 700;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.wsim-submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.3);
}

.wsim-submit-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ── Form ── */
.wsim-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.wsim-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  text-align: left;
}

.wsim-label {
  font-size: 13.5px;
  font-weight: 700;
  color: #1e293b;
}

.wsim-input {
  width: 100%;
  padding: 11px 14px;
  font-size: 14px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;
  font-family: inherit;
}

.wsim-input:focus {
  border-color: #818cf8;
  background: white;
  box-shadow: 0 0 0 3px rgba(129, 140, 248, 0.12);
}

.wsim-input::placeholder {
  color: #94a3b8;
}

/* ── Yes/No toggle ── */
.wsim-yesno {
  display: flex;
  gap: 10px;
}

.wsim-yn-btn {
  flex: 1;
  padding: 10px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  font-size: 14px;
  font-weight: 700;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
}

.wsim-yn-btn:hover {
  border-color: #c7d2fe;
  background: #f5f3ff;
}

.wsim-yn-btn.active {
  border-color: #818cf8;
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  color: #4338ca;
  box-shadow: 0 0 0 3px rgba(129, 140, 248, 0.15);
}

/* ── Choice pills ── */
.wsim-choices {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.wsim-choice-pill {
  padding: 8px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 100px;
  background: #f8fafc;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.wsim-choice-pill:hover {
  border-color: #c7d2fe;
  background: #f5f3ff;
}

.wsim-choice-pill.active {
  border-color: #818cf8;
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  color: #4338ca;
  box-shadow: 0 0 0 3px rgba(129, 140, 248, 0.15);
}

/* ── Toast ── */
.wsim-toast {
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  background: #0f172a;
  color: white;
  padding: 8px 20px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

.wsim-toast-anim-enter-active {
  animation: wsim-toast-in 0.3s ease;
}
.wsim-toast-anim-leave-active {
  animation: wsim-toast-in 0.3s ease reverse;
}

@keyframes wsim-toast-in {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

/* ── Mobile ── */
@media (max-width: 640px) {
  .wsim-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .wsim-card {
    padding: 14px 8px 12px;
    gap: 6px;
  }

  .wsim-card-emoji {
    width: 40px;
    height: 40px;
    font-size: 20px;
    border-radius: 10px;
  }

  .wsim-card-name {
    font-size: 13px;
  }

  .wsim-card-hint {
    display: none;
  }

  .wsim-title {
    font-size: 18px;
  }

  .wsim-sub {
    font-size: 13px;
  }

  .wsim-products {
    grid-template-columns: repeat(2, 1fr);
  }

  .wsim-prod-img {
    width: 60px;
    height: 60px;
  }
}
</style>
